import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  writeBatch,
  orderBy,
  collectionGroup,
  where,
} from "firebase/firestore";
import { db } from "../../../firebaseConfig"; // Adjust path as necessary

// New components for forms
import VolumeForm from "./VolumeForm";
import IssueForm from "./IssueForm";
import ManageArticles from "./ManageArticles";

function ManageIssues() {
  const [volumes, setVolumes] = useState([]);
  const [selectedVolume, setSelectedVolume] = useState(null);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal/Form states
  const [isVolumeFormOpen, setIsVolumeFormOpen] = useState(false);
  const [currentVolume, setCurrentVolume] = useState(null); // For editing a volume

  const [isIssueFormOpen, setIsIssueFormOpen] = useState(false);
  const [currentIssue, setCurrentIssue] = useState(null); // For editing an issue

  const [isArticlesModalOpen, setIsArticlesModalOpen] = useState(false);
  const [issueForArticles, setIssueForArticles] = useState(null);

  const volumesCollectionRef = collection(db, "journalVolumes");

  useEffect(() => {
    document.title = "AJORIST - Manage Issues";
    fetchVolumes();
  }, []);

  useEffect(() => {
    if (selectedVolume) {
      fetchIssues(selectedVolume.id);
    } else {
      setIssues([]); // Clear issues if no volume is selected
    }
  }, [selectedVolume]);

  const fetchVolumes = async () => {
    setLoading(true);
    try {
      const q = query(volumesCollectionRef, orderBy("order", "asc"));
      const data = await getDocs(q);
      const fetchedVolumes = data.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }));
      setVolumes(fetchedVolumes);
      // Automatically select the first volume if available and no volume is currently selected
      if (fetchedVolumes.length > 0 && !selectedVolume) {
        setSelectedVolume(fetchedVolumes[0]);
      } else if (fetchedVolumes.length === 0) {
        setSelectedVolume(null);
      } else if (
        selectedVolume &&
        !fetchedVolumes.some((v) => v.id === selectedVolume.id)
      ) {
        // If previously selected volume was deleted, select the first one
        setSelectedVolume(fetchedVolumes[0]);
      }
    } catch (error) {
      console.error("Error fetching volumes:", error);
      alert("Failed to load volumes.");
    } finally {
      setLoading(false);
    }
  };

  const fetchIssues = async (volumeId) => {
    if (!volumeId) {
      setIssues([]);
      return;
    }
    try {
      const issuesCollectionRef = collection(
        db,
        `journalVolumes/${volumeId}/issues`,
      );
      const q = query(issuesCollectionRef, orderBy("order", "asc"));
      const data = await getDocs(q);
      const fetchedIssues = data.docs
        .map((docSnapshot) => {
          const issueData = docSnapshot.data();
          // Ensure articles array exists and sort it by the 'order' field
          issueData.articles = (issueData.articles || []).sort(
            (a, b) => a.order - b.order,
          );
          return { ...issueData, id: docSnapshot.id };
        })
        .sort((a, b) => a.order - b.order); // Keep sorting issues by their order too

      setIssues(fetchedIssues);
    } catch (error) {
      console.error("Error fetching issues:", error);
      alert("Failed to load issues for this volume.");
    }
  };

  // Volume CRUD handlers
  const handleAddVolume = () => {
    setCurrentVolume(null); // Clear for new volume
    setIsVolumeFormOpen(true);
  };

  const handleEditVolume = (volume) => {
    setCurrentVolume(volume);
    setIsVolumeFormOpen(true);
  };

  const handleDeleteVolume = async (volumeId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this volume and all its issues? This action cannot be undone.",
      )
    ) {
      try {
        await deleteDoc(doc(db, "journalVolumes", volumeId));
        fetchVolumes(); // Refresh list
        // setSelectedVolume(null); // This will be handled by fetchVolumes
      } catch (error) {
        console.error("Error deleting volume:", error);
        alert("Failed to delete volume.");
      }
    }
  };

  // Issue CRUD handlers
  const handleAddIssue = () => {
    if (!selectedVolume) {
      alert("Please select a volume first to add an issue.");
      return;
    }
    setCurrentIssue(null); // Clear for new issue
    setIsIssueFormOpen(true);
  };

  const handleEditIssue = (issue) => {
    setCurrentIssue(issue);
    setIsIssueFormOpen(true);
  };

  const handleDeleteIssue = async (issueId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this issue? This action cannot be undone.",
      )
    ) {
      try {
        await deleteDoc(
          doc(db, `journalVolumes/${selectedVolume.id}/issues`, issueId),
        );
        fetchIssues(selectedVolume.id); // Refresh issues
      } catch (error) {
        console.error("Error deleting issue:", error);
        alert("Failed to delete issue.");
      }
    }
  };
  const handleManageArticles = (issue) => {
    setIssueForArticles(issue);
    setIsArticlesModalOpen(true);
  };

  const handleSetLatestIssue = async (issueToSet) => {
    if (issueToSet.isLatest) {
      alert("This is already the latest issue.");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to set "${issueToSet.title}" as the new latest issue?`,
      )
    ) {
      return;
    }

    const batch = writeBatch(db);

    try {
      // 1. Find and unset the current latest issue(s)
      const issuesCollectionGroup = collectionGroup(db, "issues");
      const latestQuery = query(
        issuesCollectionGroup,
        where("isLatest", "==", true),
      );
      const latestSnapshot = await getDocs(latestQuery);
      latestSnapshot.forEach((doc) => {
        batch.update(doc.ref, { isLatest: false });
      });

      // 2. Set the new latest issue
      const newLatestRef = doc(
        db,
        `journalVolumes/${selectedVolume.id}/issues`,
        issueToSet.id,
      );
      batch.update(newLatestRef, { isLatest: true });

      // 3. Commit the batch and refresh
      await batch.commit();
      fetchIssues(selectedVolume.id); // Refresh the view
      alert(`Successfully set "${issueToSet.title}" as the latest issue.`);
    } catch (error) {
      console.error("Error setting latest issue:", error);
      alert("An error occurred. Please try again.");
    }
  };
  return (
    <div className="flex h-full">
      {/* Volumes Sidebar */}
      <div className="w-1/3 border-r border-slate-200 p-4 overflow-y-auto bg-slate-50">
        <h2 className="text-xl font-bold mb-4 text-slate-800">Volumes</h2>
        <button
          onClick={handleAddVolume}
          className="mb-4 bg-sky-600 hover:bg-sky-700 text-white font-bold py-2 px-4 rounded text-sm shadow-md"
        >
          Add New Volume
        </button>
        {loading ? (
          <p className="text-slate-500">Loading volumes...</p>
        ) : volumes.length === 0 ? (
          <p className="text-slate-500">No volumes found.</p>
        ) : (
          <ul className="space-y-2">
            {volumes.map((volume) => (
              <li
                key={volume.id}
                className={`p-2 rounded cursor-pointer flex justify-between items-center transition-colors ${
                  selectedVolume?.id === volume.id
                    ? "bg-sky-100 text-sky-800 font-semibold"
                    : "hover:bg-slate-100 text-slate-700"
                }`}
                onClick={() => setSelectedVolume(volume)}
              >
                <span>
                  {volume.title} ({volume.year})
                </span>
                <div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditVolume(volume);
                    }}
                    className="text-sky-600 hover:text-sky-800 text-sm mr-2"
                  >
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteVolume(volume.id);
                    }}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Issues Main Content */}
      <div className="flex-1 p-4 overflow-y-auto">
        {selectedVolume ? (
          <>
            <h2 className="text-xl font-bold mb-4 text-slate-800">
              Issues for {selectedVolume.title} ({selectedVolume.year})
            </h2>
            <button
              onClick={handleAddIssue}
              className="mb-4 bg-sky-600 hover:bg-sky-700 text-white font-bold py-2 px-4 rounded text-sm shadow-md"
            >
              Add New Issue
            </button>
            {issues.length === 0 ? (
              <p className="text-slate-500">No issues for this volume.</p>
            ) : (
              <table className="min-w-full leading-normal bg-white shadow-md rounded-lg">
                <thead>
                  <tr>
                    <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {issues.map((issue) => (
                    <tr key={issue.id} className="hover:bg-slate-50">
                      <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm text-slate-700">
                        {issue.title}
                      </td>
                      <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm text-slate-700">
                        {issue.date}
                      </td>
                      <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                        <button
                          onClick={() => handleEditIssue(issue)}
                          className="text-sky-600 hover:text-sky-800 text-sm mr-2"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleSetLatestIssue(issue)}
                          disabled={issue.isLatest}
                          className={`text-sm mr-2 ${
                            issue.isLatest
                              ? "text-yellow-500 font-bold"
                              : "text-yellow-600 hover:text-yellow-800"
                          }`}
                        >
                          {issue.isLatest ? "✓ Latest" : "Set as Latest"}
                        </button>
                        <button
                          onClick={() => handleManageArticles(issue)}
                          className="text-green-600 hover:text-green-800 text-sm mr-2"
                        >
                          Articles
                        </button>
                        <button
                          onClick={() => handleDeleteIssue(issue.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        ) : (
          <p className="text-slate-500">
            Select a volume from the left to manage its issues.
          </p>
        )}
      </div>

      {/* Volume Form Modal */}
      {isVolumeFormOpen && (
        <VolumeForm
          volume={currentVolume}
          onClose={() => setIsVolumeFormOpen(false)}
          onSave={fetchVolumes} // Refresh volumes after save
        />
      )}

      {/* Issue Form Modal */}
      {isIssueFormOpen && selectedVolume && (
        <IssueForm
          volumeId={selectedVolume.id}
          issue={currentIssue}
          onClose={() => setIsIssueFormOpen(false)}
          onSave={() => fetchIssues(selectedVolume.id)} // Refresh issues after save
        />
      )}

      {/* Articles Modal */}
      {isArticlesModalOpen && (
        <ManageArticles
          volumeId={selectedVolume?.id}
          issue={issueForArticles}
          onClose={() => setIsArticlesModalOpen(false)}
          onSave={() => fetchIssues(selectedVolume.id)}
        />
      )}
    </div>
  );
}

export default ManageIssues;
